/**
 * As premissas do cenário, ditas em português (spec-007, US-3).
 *
 * ★ POR QUE ESTE ARQUIVO EXISTE — e o que foi MEDIDO em 24/08/2026, depois de
 * o QA ver `1 / 0.1 / 0.01 / 5 / 5 / assessoria / fb / [object Object] / 60`
 * no cartão (cliente 17765563):
 *
 * O backend **não manda frases**. `_premissas_declaradas`
 * (`aawz_fee_fixo/core/simulacao_chat.py:337`) devolve um mapa
 * **campo → VALOR CRU** — a docstring de lá diz "as hipóteses do CENÁRIO,
 * NOMEADAS", e o nome está na CHAVE, não numa frase. O cartão jogava a chave
 * fora (`String(declaradas[chave] || '')`) e imprimia só o valor, o que
 * produzia três defeitos de uma vez:
 *
 * 1. **número sem grandeza** — `0.1` não diz "retorno de 10% ao ano";
 * 2. **`[object Object]`** — `alloc` é um dicionário por classe;
 * 3. ★ **omissão silenciosa** — `|| ''` seguido de `filter(Boolean)` apagava
 *    `aporteAnual: 0` e `plataforma: null`. Zero é valor LEGÍTIMO (a fixture
 *    do backend registra isso na própria linha), e a lista passava a mentir
 *    por omissão sobre o que foi declarado. Era o defeito menos visível dos
 *    três e o mais grave: os outros dois se veem na tela.
 *
 * Por isso a tradução mora aqui e não no `.vue`: ela tem vocabulário
 * (`KLABEL`, `PRESET_NAME`, `ROTULO_DA_PLATAFORMA`) e regra de arredondamento,
 * que é derivação — e derivação testa-se sem montar componente.
 *
 * ⚠️ Nada aqui RECALCULA premissa: o valor exibido é o que veio no envelope.
 * Se um dia o backend passar a mandar a frase pronta, o certo é esta lista
 * passar a exibi-la, não as duas coisas conviverem.
 */
import { KLABEL, K, PRESET_NAME } from '@modules/fee-fixo/constants/feeLab'
import {
  CANAL_ASSESSORIA,
  CANAL_CONSULTORIA,
} from '@modules/fee-fixo/constants/baldes'
import { ROTULO_DA_PLATAFORMA } from '@modules/fee-fixo/constants/plataformas'
import { bps, fmtBRL, pct } from '@modules/fee-fixo/utils/fee-lab-format'
import { pluralize } from '@utils/Format'

// O canal como NOME DO MODELO de atendimento, não como cargo: aqui a premissa
// é o regime ("Assessoria"), e `termoDoProfissional` responde a outra pergunta
// ("assessor"). As chaves vêm de `constants/baldes.js` — redigitá-las faria a
// linha sumir no dia em que o valor do backend mudasse.
export const ROTULO_DO_CANAL = {
  [CANAL_ASSESSORIA]: 'Assessoria',
  [CANAL_CONSULTORIA]: 'Consultoria CVM',
}

/**
 * A ordem em que as premissas aparecem — a MESMA de `PREMISSAS_DA_US_3`
 * (`simulacao_chat.py:114`): a hipótese do cenário, depois o caso, depois o
 * perfil de alocação, e o fee proposto por último.
 *
 * Chave que o backend acrescentar e não estiver aqui **continua aparecendo**,
 * no fim e com o nome cru — a regra da casa é que chave nova apareça feia em
 * vez de sumir.
 */
export const ORDEM_DAS_PREMISSAS = [
  'cb_dev',
  'rent',
  'alfa',
  'aporteAnual',
  'hor',
  'mig',
  'canal',
  'plataforma',
  'allocKey',
  'alloc',
  'fee_prof',
]

export const ROTULO_DA_PREMISSA = {
  cb_dev: 'Cashback devolvido ao cliente',
  rent: 'Retorno esperado da carteira',
  alfa: 'Alfa do serviço fee-based',
  aporteAnual: 'Aporte anual',
  hor: 'Horizonte da projeção',
  mig: 'Prazo de migração',
  canal: 'Canal de atendimento',
  plataforma: 'Plataforma',
  allocKey: 'Alocação-alvo',
  alloc: 'Composição da alocação-alvo',
  fee_prof: 'Fee do profissional',
}

/** Anos com plural correto — `hor` e `mig` chegam como número. */
const emAnos = (valor) => {
  const n = Number(valor) || 0
  return n + ' ' + pluralize(n, 'ano', 'anos')
}

/**
 * A composição por classe, só o que tem peso.
 *
 * Classe zerada não entra: a alocação-alvo tem sete classes e a maioria dos
 * cenários usa metade delas — listar "Float 0%" seis vezes afogaria as que
 * importam. Ausência aqui NÃO é omissão silenciosa (o rótulo diz "composição",
 * e peso zero não é composição); é diferente do `aporteAnual: 0`, que é a
 * premissa inteira.
 *
 * A ordem é a de `K`, a mesma da grade da tela — `Object.keys` daria a ordem
 * de inserção do JSON, que muda com o backend.
 */
const composicao = (valor) => {
  if (!valor || typeof valor !== 'object') return ''
  const partes = K.filter((classe) => Number(valor[classe]) > 0).map(
    (classe) => (KLABEL[classe] || classe) + ' ' + pct(valor[classe])
  )
  return partes.join(' · ')
}

const FORMATO_DA_PREMISSA = {
  // Frações → percentual. `cb_dev: 1` é "100%", que é o que "cashback integral"
  // quer dizer sem exigir que o consultor saiba a unidade.
  cb_dev: pct,
  rent: pct,
  alfa: pct,
  aporteAnual: fmtBRL,
  hor: emAnos,
  mig: emAnos,
  canal: (valor) => ROTULO_DO_CANAL[valor] || String(valor),
  plataforma: (valor) => ROTULO_DA_PLATAFORMA[valor] || String(valor),
  allocKey: (valor) => PRESET_NAME[valor] || String(valor),
  alloc: composicao,
  fee_prof: bps,
}

/**
 * Último recurso para chave que o backend acrescentou e ninguém traduziu.
 *
 * O que ele NÃO pode fazer é `String(objeto)`: `[object Object]` é a única
 * saída pior que a chave crua, porque parece conteúdo. Objeto desconhecido sai
 * como pares `chave: valor`; o resto sai como veio.
 */
const formatoGenerico = (valor) => {
  if (valor && typeof valor === 'object') {
    return Object.keys(valor)
      .map((chave) => chave + ': ' + valor[chave])
      .join(' · ')
  }
  return String(valor)
}

/**
 * ★ Ausente é `undefined`/`null`/`''` — e **`0` NÃO é ausente**. Foi o `|| ''`
 * que apagou `aporteAnual: 0` da tela; a régua aqui é a presença do valor, não
 * a verdade dele.
 */
const declarada = (valor) => valor !== undefined && valor !== null && valor !== ''

/**
 * As linhas prontas para a tela.
 *
 * @param {Object=} declaradas O `premissas_declaradas` do envelope da tool.
 * @returns {Array<{chave: string, rotulo: string, valor: string}>} Uma linha
 *   por premissa declarada, na ordem do contrato. Linha com valor vazio depois
 *   de formatada é descartada (é o caso da composição sem classe com peso).
 */
export const linhasDasPremissasDeclaradas = (declaradas) => {
  const mapa = declaradas || {}
  const conhecidas = ORDEM_DAS_PREMISSAS.filter((chave) => chave in mapa)
  const novas = Object.keys(mapa).filter(
    (chave) => ORDEM_DAS_PREMISSAS.indexOf(chave) === -1
  )
  return conhecidas
    .concat(novas)
    .filter((chave) => declarada(mapa[chave]))
    .map((chave) => {
      const formato = FORMATO_DA_PREMISSA[chave] || formatoGenerico
      return {
        chave,
        rotulo: ROTULO_DA_PREMISSA[chave] || chave,
        valor: String(formato(mapa[chave])).trim(),
      }
    })
    .filter((linha) => linha.valor)
}
